import {useCallback, useState} from 'react';

import AutosaveManager from 'procedures/redux/autosaveManager';

import {MethodEditorTabEditingState} from './MethodEditorTabShared';

interface UseStartEditingLegacyDefinitionsParams {
  isEditingMethodTab: boolean;
  procedureId: string;
  updateIsEditingState: (newState: boolean) => void;
}

interface UseStartEditingLegacyDefinitionsReturn {
  handleOnStartEditing: () => void;
  editingState: MethodEditorTabEditingState;
}

/**
 * Shared hook for legacy procedure definition editors (MethodDefinitionTables, TestTablesEditor).
 * Manages the STARTING_EDITING intermediate state while flushing unsaved changes before
 * transitioning to edit mode, mirroring the PoW behavior in useDraftMethodEditor.
 */
export default function useStartEditingLegacyDefinitions({
  isEditingMethodTab,
  procedureId,
  updateIsEditingState,
}: UseStartEditingLegacyDefinitionsParams): UseStartEditingLegacyDefinitionsReturn {
  const [isStartingEditing, setIsStartingEditing] = useState(false);

  const handleOnStartEditing = useCallback(async () => {
    if (isStartingEditing || isEditingMethodTab) {
      return;
    }
    const timeout = setTimeout(() => {
      setIsStartingEditing(true);
    }, 150);
    try {
      await AutosaveManager.flushUnsavedChanges(procedureId);
    } finally {
      clearTimeout(timeout);
      setIsStartingEditing(false);
    }
    updateIsEditingState(true);
  }, [isStartingEditing, isEditingMethodTab, procedureId, updateIsEditingState]);

  const editingState = isStartingEditing
    ? MethodEditorTabEditingState.STARTING_EDITING
    : isEditingMethodTab
      ? MethodEditorTabEditingState.EDITING
      : MethodEditorTabEditingState.NOT_EDITING;

  return {handleOnStartEditing, editingState};
}
